import {
  OmsConditionStep,
  StepType,
} from "@redotech/redo-model/advanced-flow/steps";
import { AutomationForgeElementType } from "@redotech/redo-model/forge/marketing-automation-forge-types";
import { assertNever } from "@redotech/util/type";
import { Edge as XyFlowEdge } from "@xyflow/react";
import { FlowInProgress } from "./flow-editor";
import { AbTestNodeData } from "./nodes/ab-test-node";
import { BlockCheckoutNodeData } from "./nodes/block-checkout-node";
import { BranchNodeData } from "./nodes/branch-node";
import { CheckoutExperienceNodeData } from "./nodes/checkout-experience-node";
import { DownsellNodeData } from "./nodes/downsell-node";
import { DropZoneNodeData } from "./nodes/drop-zone-node";
import { EndNodeData } from "./nodes/end-node";
import { OmsActionNodeData } from "./nodes/oms/oms-action-node";
import { OmsConditionNodeData } from "./nodes/oms/oms-condition-node";
import {
  getPostPurchasePageEdgesAndDropZones,
  PostPurchasePageNodeData,
} from "./nodes/post-purchase-page-node";
import {
  getPostPurchaseSegmentEdgesAndDropZones,
  PostPurchaseSegmentNodeData,
} from "./nodes/post-purchase-segment-node";
import { PostPurchaseSplitNodeData } from "./nodes/post-purchase-split-node";
import { ProcessReturnNodeData } from "./nodes/process-return-node";
import { RcoCheckoutPackageConditionNodeData } from "./nodes/rco-checkout-package-condition-node";
import { RcoCheckoutPackageNodeData } from "./nodes/rco-checkout-package-node";
import {
  getRcoNarySegmentEdgesAndDropZones,
  getRcoSegmentEdgesAndDropZones,
  isNarySegmentStep,
  RcoSegmentNodeData,
} from "./nodes/rco-segment-node";
import { RcoSplitNodeData } from "./nodes/rco-split-node";
import { RecoverNodeData } from "./nodes/recover-node";
import { SendMessageNodeData } from "./nodes/send-message-node";
import { SendWebhookNodeData } from "./nodes/send-webhook-node";
import {
  getTrackingPageEdgesAndDropZones,
  TrackingPageNodeData,
} from "./nodes/tracking-page-node";
import { TriggerNodeData } from "./nodes/trigger-node";
import { UpsellConditionNodeData } from "./nodes/upsell-condition-node";
import { UpsellNodeData } from "./nodes/upsell-node";
import { WaitForEventNodeData } from "./nodes/wait-for-event-node";
import { WaitNodeData } from "./nodes/wait-node";
import { WorkOrderConditionNodeData } from "./nodes/work-order/work-order-condition-node";
import { WorkOrderWorkStepNodeData } from "./nodes/work-order/work-order-work-step-node";
import { PartialFlowStep } from "./steps";

export enum NodeTypes {
  // GENERAL
  TRIGGER = "trigger",
  DROP_ZONE = "dropZone",
  END = "end",

  // MARKETING
  WAIT = "wait",
  WAIT_FOR_EVENT = "wait_for_event",
  BRANCH = "branch",
  SEND_MESSAGE = "sendMessage",
  SEND_WEBHOOK = "sendWebhook",
  CUSTOMER_TAG_ACTION = "customer_tag_action",
  MANAGE_STATIC_SEGMENT = "manage_static_segment",
  RECOVER = "recover",
  UPSELL = "upsell",
  DOWNSELL = "downsell",
  UPSELL_CONDITION = "upsell_condition",
  AB_TEST = "ab_test",

  // OMS
  OMS_ACTION = "oms_action",
  OMS_CONDITION = "oms_condition",

  // CHECKOUT PERSONALIZATION
  RCO_SPLIT = "rco_split",
  CHECKOUT_EXPERIENCE = "checkout_experience",
  BLOCK_CHECKOUT = "block_checkout",
  RCO_SEGMENT = "rco_segment",

  // CHECKOUT PACKAGE
  RCO_CHECKOUT_PACKAGE = "rco_checkout_package",
  RCO_CHECKOUT_PACKAGE_CONDITION = "rco_checkout_package_condition",

  // POST-PURCHASE
  POST_PURCHASE_PAGE = "post_purchase_page",
  POST_PURCHASE_SEGMENT = "post_purchase_segment",
  POST_PURCHASE_SPLIT = "post_purchase_split",

  // TRACKING PAGE
  TRACKING_PAGE = "tracking_page",

  // SUPPORT RULES
  RULE_CONDITIONS = "rule_conditions",
  RULE_ACTIONS = "rule_actions",

  // Work order flows
  WORK_ORDER_WORK_STEP = "work_order_work_step",
  WORK_ORDER_CONDITION = "work_order_condition",

  // Return processing flows
  PROCESS_RETURN = "process_return",
}

interface BaseNodeProps<T extends NodeTypes, D extends Record<string, any>> {
  id: string;
  type: T;
  data: D & Record<string, unknown>;
  position: { x: number; y: number };
  selected?: boolean;
}

type TriggerNodeProps = BaseNodeProps<NodeTypes.TRIGGER, TriggerNodeData>;
type SendMessageNodeProps = BaseNodeProps<
  NodeTypes.SEND_MESSAGE,
  SendMessageNodeData
>;
type SendWebhookNodeProps = BaseNodeProps<
  NodeTypes.SEND_WEBHOOK,
  SendWebhookNodeData
>;
type CustomerTagActionNodeProps = BaseNodeProps<
  NodeTypes.CUSTOMER_TAG_ACTION,
  { step: PartialFlowStep; deleteElements: (id: string) => void }
>;
type ManageStaticSegmentNodeProps = BaseNodeProps<
  NodeTypes.MANAGE_STATIC_SEGMENT,
  { step: PartialFlowStep; deleteElements: (id: string) => void }
>;
type RecoverNodeProps = BaseNodeProps<NodeTypes.RECOVER, RecoverNodeData>;
type WaitForEventNodeProps = BaseNodeProps<
  NodeTypes.WAIT_FOR_EVENT,
  WaitForEventNodeData
>;
type WaitNodeProps = BaseNodeProps<NodeTypes.WAIT, WaitNodeData>;
type BranchNodeProps = BaseNodeProps<NodeTypes.BRANCH, BranchNodeData>;
type EndNodeProps = BaseNodeProps<NodeTypes.END, EndNodeData>;
type DropZoneNodeProps = BaseNodeProps<NodeTypes.DROP_ZONE, DropZoneNodeData>;
type UpsellNodeProps = BaseNodeProps<NodeTypes.UPSELL, UpsellNodeData>;
type DownsellNodeProps = BaseNodeProps<NodeTypes.DOWNSELL, DownsellNodeData>;
type UpsellConditionNodeProps = BaseNodeProps<
  NodeTypes.UPSELL_CONDITION,
  UpsellConditionNodeData
>;
// OMS
type OmsActionNodeProps = BaseNodeProps<
  NodeTypes.OMS_ACTION,
  OmsActionNodeData
>;
type OmsConditionNodeProps = BaseNodeProps<
  NodeTypes.OMS_CONDITION,
  OmsConditionNodeData
>;
// Checkout personalization
type RcoSplitNodeProps = BaseNodeProps<NodeTypes.RCO_SPLIT, RcoSplitNodeData>;
type CheckoutExperienceNodeProps = BaseNodeProps<
  NodeTypes.CHECKOUT_EXPERIENCE,
  CheckoutExperienceNodeData
>;
type BlockCheckoutNodeProps = BaseNodeProps<
  NodeTypes.BLOCK_CHECKOUT,
  BlockCheckoutNodeData
>;
type RcoSegmentNodeProps = BaseNodeProps<
  NodeTypes.RCO_SEGMENT,
  RcoSegmentNodeData
>;

// Checkout package
type RcoCheckoutPackageNodeProps = BaseNodeProps<
  NodeTypes.RCO_CHECKOUT_PACKAGE,
  RcoCheckoutPackageNodeData
>;
type RcoCheckoutPackageConditionNodeProps = BaseNodeProps<
  NodeTypes.RCO_CHECKOUT_PACKAGE_CONDITION,
  RcoCheckoutPackageConditionNodeData
>;

type AbTestNodeProps = BaseNodeProps<NodeTypes.AB_TEST, AbTestNodeData>;

// Post-purchase node props
type PostPurchasePageNodeProps = BaseNodeProps<
  NodeTypes.POST_PURCHASE_PAGE,
  PostPurchasePageNodeData
>;
type PostPurchaseSegmentNodeProps = BaseNodeProps<
  NodeTypes.POST_PURCHASE_SEGMENT,
  PostPurchaseSegmentNodeData
>;
type PostPurchaseSplitNodeProps = BaseNodeProps<
  NodeTypes.POST_PURCHASE_SPLIT,
  PostPurchaseSplitNodeData
>;

// Tracking page node props
type TrackingPageNodeProps = BaseNodeProps<
  NodeTypes.TRACKING_PAGE,
  TrackingPageNodeData
>;

// Support rules node props
type RuleConditionsNodeProps = BaseNodeProps<
  NodeTypes.RULE_CONDITIONS,
  {
    step: PartialFlowStep;
    deleteElements: (id: string) => void;
    updateStep: (id: string, updatedStep: PartialFlowStep) => void;
    readonly: boolean;
  }
>;
type RuleActionsNodeProps = BaseNodeProps<
  NodeTypes.RULE_ACTIONS,
  { step: PartialFlowStep; deleteElements: (id: string) => void }
>;
type WorkOrderWorkStepNodeProps = BaseNodeProps<
  NodeTypes.WORK_ORDER_WORK_STEP,
  WorkOrderWorkStepNodeData
>;
type WorkOrderConditionNodeProps = BaseNodeProps<
  NodeTypes.WORK_ORDER_CONDITION,
  WorkOrderConditionNodeData
>;
type ProcessReturnNodeProps = BaseNodeProps<
  NodeTypes.PROCESS_RETURN,
  ProcessReturnNodeData
>;

export type TypedFlowNode =
  | TriggerNodeProps
  | SendMessageNodeProps
  | SendWebhookNodeProps
  | CustomerTagActionNodeProps
  | ManageStaticSegmentNodeProps
  | RecoverNodeProps
  | WaitNodeProps
  | WaitForEventNodeProps
  | BranchNodeProps
  | EndNodeProps
  | DropZoneNodeProps
  | UpsellNodeProps
  | DownsellNodeProps
  | UpsellConditionNodeProps
  | OmsActionNodeProps
  | OmsConditionNodeProps
  | RcoSplitNodeProps
  | CheckoutExperienceNodeProps
  | BlockCheckoutNodeProps
  | RcoSegmentNodeProps
  | RcoCheckoutPackageNodeProps
  | RcoCheckoutPackageConditionNodeProps
  | AbTestNodeProps
  | PostPurchasePageNodeProps
  | PostPurchaseSegmentNodeProps
  | PostPurchaseSplitNodeProps
  | TrackingPageNodeProps
  | RuleConditionsNodeProps
  | RuleActionsNodeProps
  | WorkOrderWorkStepNodeProps
  | WorkOrderConditionNodeProps
  | ProcessReturnNodeProps;

export type FlowEdge = XyFlowEdge;

export function buildNodesAndEdges({
  automation,
  elementDropped,
  deleteElements,
  updateStep,
  selected,
  readonly,
  hoveredNodeId,
}: {
  automation: FlowInProgress;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
    edgeId?: string,
  ) => void;
  deleteElements: (id: string) => void;
  updateStep: (id: string, updatedStep: PartialFlowStep) => void;
  selected: string | undefined;
  readonly: boolean;
  hoveredNodeId?: string | null;
}) {
  const nodes: TypedFlowNode[] = automation.steps.map(
    (step: PartialFlowStep) => {
      switch (step.type) {
        case StepType.TRIGGER:
          return {
            id: step.id,
            type: NodeTypes.TRIGGER,
            position: { x: 0, y: 0 },
            data: {
              step,
              deleteElements,
              triggerConditions: automation.triggerConditions,
              triggerSource: automation.triggerSource,
              triggerBomIds: automation.triggerBomIds,
              flowName:
                typeof automation.name === "string"
                  ? automation.name
                  : automation.name?.value,
            },
            selected: selected === step.id,
          };
        case StepType.WAIT:
          return {
            id: step.id,
            type: NodeTypes.WAIT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.WAIT_FOR_EVENT:
          return {
            id: step.id,
            type: NodeTypes.WAIT_FOR_EVENT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.CONDITION:
          return {
            id: step.id,
            type: NodeTypes.BRANCH,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, updateStep, readonly },
            selected: selected === step.id,
          };
        case StepType.DO_NOTHING:
          return {
            id: step.id,
            type: NodeTypes.END,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.SEND_EMAIL:
          return {
            id: step.id,
            type: NodeTypes.SEND_MESSAGE,
            position: { x: 0, y: 0 },
            data: {
              step,
              deleteElements,
              isEmail: true,
              automationId: automation._id.toString(),
              templateId: step.templateId,
              originTemplateId: step.originTemplateId,
            },
            selected: selected === step.id,
          };
        case StepType.SEND_SMS:
          return {
            id: step.id,
            type: NodeTypes.SEND_MESSAGE,
            position: { x: 0, y: 0 },
            data: {
              step,
              deleteElements,
              isEmail: false,
              automationId: automation._id.toString(),
              templateId: step.templateId,
              originTemplateId: undefined,
            },
            selected: selected === step.id,
          };
        case StepType.UPSELL:
          return {
            id: step.id,
            type: NodeTypes.UPSELL,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.DOWNSELL:
          return {
            id: step.id,
            type: NodeTypes.DOWNSELL,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.UPSELL_CONDITION:
          return {
            id: step.id,
            type: NodeTypes.UPSELL_CONDITION,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.OMS_ACTION:
          return {
            id: step.id,
            type: NodeTypes.OMS_ACTION,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.OMS_CONDITION:
          return {
            id: step.id,
            type: NodeTypes.OMS_CONDITION,
            position: { x: 0, y: 0 },
            data: { step: step as OmsConditionStep, deleteElements }, //FIXME once OMS automations are migrated to the new zod types
            selected: selected === step.id,
          };
        case StepType.RCO_SPLIT:
          return {
            id: step.id,
            type: NodeTypes.RCO_SPLIT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.CHECKOUT_EXPERIENCE:
          return {
            id: step.id,
            type: NodeTypes.CHECKOUT_EXPERIENCE,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.BLOCK_CHECKOUT:
          return {
            id: step.id,
            type: NodeTypes.BLOCK_CHECKOUT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.RCO_SEGMENT:
          return {
            id: step.id,
            type: NodeTypes.RCO_SEGMENT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, updateStep, readonly },
            selected: selected === step.id,
          };
        case StepType.RCO_CHECKOUT_PACKAGE:
          return {
            id: step.id,
            type: NodeTypes.RCO_CHECKOUT_PACKAGE,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.RCO_CHECKOUT_PACKAGE_CONDITION:
          return {
            id: step.id,
            type: NodeTypes.RCO_CHECKOUT_PACKAGE_CONDITION,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.AB_TEST:
          return {
            id: step.id,
            type: NodeTypes.AB_TEST,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.POST_PURCHASE_SPLIT:
          return {
            id: step.id,
            type: NodeTypes.POST_PURCHASE_SPLIT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.POST_PURCHASE_PAGE:
          return {
            id: step.id,
            type: NodeTypes.POST_PURCHASE_PAGE,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.POST_PURCHASE_SEGMENT:
          return {
            id: step.id,
            type: NodeTypes.POST_PURCHASE_SEGMENT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, updateStep, readonly },
            selected: selected === step.id,
          };
        case StepType.TRACKING_PAGE:
          return {
            id: step.id,
            type: NodeTypes.TRACKING_PAGE,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, readonly },
            selected: selected === step.id,
          };
        case StepType.MANAGE_CUSTOMER_TAGS:
          return {
            id: step.id,
            type: NodeTypes.CUSTOMER_TAG_ACTION,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.MANAGE_STATIC_SEGMENT:
          return {
            id: step.id,
            type: NodeTypes.MANAGE_STATIC_SEGMENT,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.SEND_WEBHOOK:
          return {
            id: step.id,
            type: NodeTypes.SEND_WEBHOOK,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.RECOVER:
          return {
            id: step.id,
            type: NodeTypes.RECOVER,
            position: { x: 0, y: 0 },
            data: {
              step,
              deleteElements,
              automationId: automation._id.toString(),
            },
            selected: selected === step.id,
          };
        case StepType.RULE_CONDITIONS:
        case StepType.RULE_CONDITIONS_V3:
          return {
            id: step.id,
            type: NodeTypes.RULE_CONDITIONS,
            position: { x: 0, y: 0 },
            data: {
              step,
              deleteElements,
              updateStep,
              readonly,
              hovered: hoveredNodeId === step.id,
            },
            selected: selected === step.id,
          };
        case StepType.RULE_ACTIONS:
        case StepType.RULE_ACTIONS_V3:
          return {
            id: step.id,
            type: NodeTypes.RULE_ACTIONS,
            position: { x: 0, y: 0 },
            data: { step, deleteElements, hovered: hoveredNodeId === step.id },
            selected: selected === step.id,
          };
        case StepType.WORK_ORDER_WORK_STEP:
          return {
            id: step.id,
            type: NodeTypes.WORK_ORDER_WORK_STEP,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.WORK_ORDER_CONDITION:
          return {
            id: step.id,
            type: NodeTypes.WORK_ORDER_CONDITION,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        case StepType.PROCESS_RETURN:
          return {
            id: step.id,
            type: NodeTypes.PROCESS_RETURN,
            position: { x: 0, y: 0 },
            data: { step, deleteElements },
            selected: selected === step.id,
          };
        default:
          assertNever(step);
      }
    },
  );

  const edges: FlowEdge[] = [];
  for (const step of automation.steps) {
    switch (step.type) {
      case StepType.TRIGGER: {
        const isCheckoutOptimizationOrPostPurchase =
          step.category === "Checkout personalization" ||
          step.category === "Post-purchase";
        const hasNode = nodes.some((node) => node.id === step.nextId);
        if (
          step.nextId !== undefined &&
          step.nextId !== "undefined" &&
          hasNode
        ) {
          edges.push({
            id: step.id + "_edge",
            type: "edge",
            source: step.id,
            target: step.nextId,
            ...(isCheckoutOptimizationOrPostPurchase
              ? {
                  targetHandle: "top",
                  ...(!readonly
                    ? {
                        data: {
                          idFrom: step.id,
                          idTo: step.nextId,
                          elementDropped,
                        },
                      }
                    : {}),
                }
              : {
                  data: { idFrom: step.id, idTo: step.nextId, elementDropped },
                }),
          });
        } else if (!readonly) {
          nodes.push({
            id: step.id + "_next",
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: { idFrom: step.id, idTo: step.nextId, elementDropped },
          });
          edges.push({
            id: step.id + "_next_edge",
            type: "dropEdge",
            source: step.id,
            target: step.id + "_next",
            data: {},
          });
        }
        break;
      }
      case StepType.WAIT:
      case StepType.WAIT_FOR_EVENT:
      case StepType.SEND_EMAIL:
      case StepType.SEND_SMS:
      case StepType.SEND_WEBHOOK:
      case StepType.RECOVER:
      case StepType.UPSELL:
      case StepType.OMS_ACTION:
      case StepType.DOWNSELL:
      case StepType.MANAGE_CUSTOMER_TAGS:
      case StepType.RULE_ACTIONS:
      case StepType.RULE_ACTIONS_V3:
      case StepType.MANAGE_STATIC_SEGMENT:
      case StepType.PROCESS_RETURN: {
        // TODO look into why we're sometimes saving "undefined" as a string and stop it
        const hasNode = nodes.some((node) => node.id === step.nextId);
        if (
          step.nextId !== undefined &&
          step.nextId !== "undefined" &&
          hasNode
        ) {
          edges.push({
            id: step.id + "_edge",
            type: "edge",
            source: step.id,
            target: (step.nextId + "").toString(), // Wanted to change this to step.nextId, but scared to break something. Seems like it's this way for a reason.
            data: { idFrom: step.id, idTo: step.nextId, elementDropped },
          });
        } else if (!readonly) {
          nodes.push({
            id: step.id + "_next",
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextId ?? undefined,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_next_edge",
            type: "dropEdge",
            source: step.id,
            target: step.id + "_next",
            data: {},
          });
        }
        break;
      }
      case StepType.WORK_ORDER_WORK_STEP: {
        const hasNode = nodes.some((node) => node.id === step.nextId);
        if (
          step.nextId !== undefined &&
          step.nextId !== "undefined" &&
          hasNode
        ) {
          edges.push({
            id: step.id + "_edge",
            type: "edge",
            source: step.id,
            target: (step.nextId + "").toString(),
            data: { idFrom: step.id, idTo: step.nextId, elementDropped },
          });
        } else if (!readonly) {
          const dropZoneId = step.id + "_next";
          nodes.push({
            id: dropZoneId,
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextId ?? undefined,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_next_edge",
            type: "dropEdge",
            source: step.id,
            target: dropZoneId,
            data: {},
          });
        }
        break;
      }
      case StepType.OMS_CONDITION:
      case StepType.CONDITION:
      case StepType.RULE_CONDITIONS:
      case StepType.RULE_CONDITIONS_V3:
        if (
          step.nextTrueId !== undefined &&
          step.nextTrueId !== "undefined" &&
          step.nextTrueId !== ""
        ) {
          edges.push({
            id: step.id + "_true",
            type: "labeled",
            source: step.id,
            sourceHandle: "a",
            target: step.nextTrueId,
            data: {
              label: "Yes",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
        } else if (!readonly) {
          nodes.push({
            id: step.id + "_true",
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_true_edge",
            type: "dropEdge",
            source: step.id,
            sourceHandle: "a",
            target: step.id + "_true",
            data: { label: "Yes" },
          });
        }
        if (
          step.nextFalseId !== undefined &&
          step.nextFalseId !== "undefined" &&
          step.nextFalseId !== ""
        ) {
          edges.push({
            id: step.id + "_false",
            type: "labeled",
            source: step.id,
            sourceHandle: "b",
            target: step.nextFalseId,
            data: {
              label: "No",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
        } else if (!readonly) {
          nodes.push({
            id: step.id + "_false",
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_false_edge",
            type: "dropEdge",
            source: step.id,
            sourceHandle: "b",
            target: step.id + "_false",
            data: { label: "No" },
          });
        }
        break;

      case StepType.WORK_ORDER_CONDITION: {
        const hasTrueNode = nodes.some((node) => node.id === step.nextTrueId);
        const hasFalseNode = nodes.some((node) => node.id === step.nextFalseId);

        if (
          step.nextTrueId !== undefined &&
          step.nextTrueId !== "undefined" &&
          step.nextTrueId !== "" &&
          hasTrueNode
        ) {
          edges.push({
            id: step.id + "_true",
            type: "labeled",
            source: step.id,
            sourceHandle: "a",
            target: step.nextTrueId,
            data: {
              label: "Yes",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
        } else if (!readonly) {
          const trueDropZoneId = step.id + "_true";
          nodes.push({
            id: trueDropZoneId,
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_true_edge",
            type: "dropEdge",
            source: step.id,
            sourceHandle: "a",
            target: trueDropZoneId,
            data: { label: "Yes" },
          });
        }
        if (
          step.nextFalseId !== undefined &&
          step.nextFalseId !== "undefined" &&
          step.nextFalseId !== "" &&
          hasFalseNode
        ) {
          edges.push({
            id: step.id + "_false",
            type: "labeled",
            source: step.id,
            sourceHandle: "b",
            target: step.nextFalseId,
            data: {
              label: "No",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
        } else if (!readonly) {
          const falseDropZoneId = step.id + "_false";
          nodes.push({
            id: falseDropZoneId,
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_false_edge",
            type: "dropEdge",
            source: step.id,
            sourceHandle: "b",
            target: falseDropZoneId,
            data: { label: "No" },
          });
        }
        break;
      }

      case StepType.UPSELL_CONDITION: {
        const hasTrueNode = nodes.some((node) => node.id === step.nextTrueId);
        if (
          step.nextTrueId !== undefined &&
          step.nextTrueId !== "undefined" &&
          hasTrueNode
        ) {
          edges.push({
            id: step.id + "_true",
            type: "edge",
            source: step.id,
            target: step.nextTrueId,
            data: {
              color: "gray",
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
        } else if (!readonly) {
          nodes.push({
            id: step.id + "_true",
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_true_edge",
            type: "dropEdge",
            source: step.id,
            target: step.id + "_true",
            data: {},
          });
        }
        break;
      }
      case StepType.RCO_SPLIT:
      case StepType.POST_PURCHASE_SPLIT: {
        if (step.treatments && step.treatments.length > 0) {
          for (const treatment of step.treatments) {
            const hasNode = nodes.some((node) => node.id === treatment?.nextId);
            if (
              treatment?.nextId !== undefined &&
              treatment.nextId !== "undefined" &&
              hasNode
            ) {
              edges.push({
                id: step.id + "_" + treatment.nextId + "_edge",
                type: "labeled",
                source: step.id,
                target: treatment.nextId,
                sourceHandle: "bottom",
                targetHandle: "top",
                data: {
                  label: treatment.name,
                  color: "gray",
                  ...(!readonly ? { elementDropped } : {}),
                  idFrom: step.id,
                  idTo: treatment.nextId,
                  edgeId: treatment.treatmentId,
                },
              });
            } else if (!readonly) {
              const dropZoneId = `${step.id}_${treatment.treatmentId}`;
              nodes.push({
                id: dropZoneId,
                type: NodeTypes.DROP_ZONE,
                position: { x: 0, y: 0 },
                data: {
                  idFrom: step.id,
                  edgeId: treatment.treatmentId,
                  elementDropped,
                },
              });
              edges.push({
                id: `${step.id}_${treatment.treatmentId}_edge`,
                type: "labeled",
                source: step.id,
                sourceHandle: "bottom",
                target: dropZoneId,
                data: { label: treatment.name, color: "gray" },
              });
            }
          }
        }
        break;
      }
      case StepType.RCO_SEGMENT: {
        if (isNarySegmentStep(step)) {
          const { edges: naryEdges, dropZoneNodes } =
            getRcoNarySegmentEdgesAndDropZones({
              stepId: step.id,
              stepEdges: step.edges,
              defaultEdgeId: step.defaultEdgeId,
              elementDropped,
              readonly,
            });
          edges.push(...naryEdges);
          nodes.push(...dropZoneNodes);
        } else {
          const { edges: rcoSegmentEdges, dropZoneNodes } =
            getRcoSegmentEdgesAndDropZones({
              stepId: step.id,
              matchingNextId:
                step.matchingNextId !== "undefined"
                  ? step.matchingNextId
                  : undefined,
              nonMatchingNextId:
                step.nonMatchingNextId !== "undefined"
                  ? step.nonMatchingNextId
                  : undefined,
              elementDropped,
              readonly,
            });
          edges.push(...rcoSegmentEdges);
          nodes.push(...dropZoneNodes);
        }
        break;
      }
      case StepType.POST_PURCHASE_PAGE: {
        const { edges: postPurchasePageEdges, dropZoneNodes } =
          getPostPurchasePageEdgesAndDropZones({
            stepId: step.id,
            acceptNextId:
              step.acceptNextId !== "undefined" ? step.acceptNextId : undefined,
            declineNextId:
              step.declineNextId !== "undefined"
                ? step.declineNextId
                : undefined,
            elementDropped,
            readonly,
          });
        edges.push(...postPurchasePageEdges);
        nodes.push(...dropZoneNodes);
        break;
      }
      case StepType.POST_PURCHASE_SEGMENT: {
        const { edges: postPurchaseSegmentEdges, dropZoneNodes } =
          getPostPurchaseSegmentEdgesAndDropZones({
            stepId: step.id,
            matchingNextId:
              step.matchingNextId !== "undefined"
                ? step.matchingNextId
                : undefined,
            nonMatchingNextId:
              step.nonMatchingNextId !== "undefined"
                ? step.nonMatchingNextId
                : undefined,
            elementDropped,
            readonly,
          });
        edges.push(...postPurchaseSegmentEdges);
        nodes.push(...dropZoneNodes);
        break;
      }
      case StepType.TRACKING_PAGE: {
        const { edges: trackingPageEdges, dropZoneNodes } =
          getTrackingPageEdgesAndDropZones({
            stepId: step.id,
            nextId: step.nextId !== "undefined" ? step.nextId : undefined,
            elementDropped,
            readonly,
          });
        edges.push(...trackingPageEdges);
        nodes.push(...dropZoneNodes);
        break;
      }
      case StepType.CHECKOUT_EXPERIENCE:
      case StepType.BLOCK_CHECKOUT:
      case StepType.RCO_CHECKOUT_PACKAGE:
      case StepType.DO_NOTHING: {
        break;
      }
      case StepType.AB_TEST: {
        if (step.variants && step.variants.length > 0) {
          for (const variant of step.variants) {
            const variantNextId = variant?.nextId;
            if (variantNextId === undefined || variantNextId === "undefined") {
              continue;
            }

            edges.push({
              id: `${step.id}_${variant.id}_to_${variantNextId}_edge`, // Make ID unique using both variant ID and target ID
              type: "labeled",
              source: step.id,
              sourceHandle: variant.id, // Use variant ID as source handle
              target: variantNextId,
              data: {
                label: variant.name,
                color: "gray",
                idFrom: step.id,
                edgeId: variant.id,
                elementDropped,
              },
            });
          }
        }
        break;
      }
      case StepType.RCO_CHECKOUT_PACKAGE_CONDITION: {
        if (step.nextTrueId !== undefined && step.nextTrueId !== "undefined") {
          edges.push({
            id: step.id + "_true_edge",
            type: "labeled",
            source: step.id,
            sourceHandle: "a",
            target: step.nextTrueId,
            data: {
              label: "Yes",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
        } else if (!readonly) {
          const dropZoneId = step.id + "_true";
          nodes.push({
            id: dropZoneId,
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextTrueId,
              conditionSide: true,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_true_edge",
            type: "labeled",
            source: step.id,
            sourceHandle: "a",
            target: dropZoneId,
            data: { label: "Yes", color: "gray" },
          });
        }
        if (
          step.nextFalseId !== undefined &&
          step.nextFalseId !== "undefined"
        ) {
          edges.push({
            id: step.id + "_false_edge",
            type: "labeled",
            source: step.id,
            sourceHandle: "b",
            target: step.nextFalseId,
            data: {
              label: "No",
              color: "gray",
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
        } else if (!readonly) {
          const dropZoneId = step.id + "_false";
          nodes.push({
            id: dropZoneId,
            type: NodeTypes.DROP_ZONE,
            position: { x: 0, y: 0 },
            data: {
              idFrom: step.id,
              idTo: step.nextFalseId,
              conditionSide: false,
              elementDropped,
            },
          });
          edges.push({
            id: step.id + "_false_edge",
            type: "labeled",
            source: step.id,
            sourceHandle: "b",
            target: dropZoneId,
            data: { label: "No", color: "gray" },
          });
        }
        break;
      }
      default:
        assertNever(step);
    }
  }

  return { nodes, edges };
}
